import { Item, ItemActions, ItemDescription, ItemHeader, ItemTitle } from '@ibsys/kit';
import { FC, ReactNode } from 'react';
import { cn } from '@/shared/lib';
import type { SpeakerSide } from '../speaker';
import { PHRASE_ROW_CLASS, PHRASE_TIME_CLASS } from './recordSide';

/** Подписанное значение строки: так перебивание называет свои реплики */
export type TranscriptRecordDetail = {
  label: string;
  value: string;
};

type Props = {
  recordId: number;
  /** Говорящий реплики или вид события */
  title: string;
  timeRange: string;
  /** Сторона ленты реплики — её цветами окрашена строка; у событий и реплики без говорящего её нет */
  side?: SpeakerSide;
  /** Текст реплики */
  text?: string;
  details?: TranscriptRecordDetail[];
  actions?: ReactNode;
  /** Реплика выбирается в поле перебивания нажатием на строку */
  onPick?: () => void;
};

/**
 * Строка ленты режима редактирования: одна запись расшифровки, окрашенная как в расшифровке вне режима.
 */
export const TranscriptRecordView: FC<Props> = ({
  recordId,
  title,
  timeRange,
  side,
  text,
  details,
  actions,
  onPick,
}) => (
  <Item
    variant={side ? 'default' : 'outline'}
    size="sm"
    data-message-id={recordId}
    role={onPick ? 'button' : undefined}
    tabIndex={onPick ? -1 : undefined}
    onClick={onPick}
    className={cn(
      'min-w-0',
      side ? PHRASE_ROW_CLASS[side] : 'text-(--color-chat-event)',
      onPick && 'cursor-pointer ring-1 ring-primary hover:opacity-80',
    )}
  >
    <ItemHeader className="min-w-0 gap-2">
      <ItemTitle className="min-w-0 wrap-break-word">{title}</ItemTitle>
      <span className={cn('ml-auto shrink-0 text-xs', side ? PHRASE_TIME_CLASS[side] : 'text-(--color-chat-event)')}>
        {timeRange}
      </span>
      {/* Действия — на фоне карточки, а не облака: значок удаления своего цвета на заливке облака не читается */}
      {actions && <ItemActions className="shrink-0 rounded-md bg-card">{actions}</ItemActions>}
    </ItemHeader>
    {/* Текст реплики — не описание строки: читается целиком, без усечения, которое kit даёт описанию */}
    {text && <p className="w-full text-sm whitespace-pre-wrap wrap-break-word">{text}</p>}
    {details?.map((detail) => (
      // Цвет описания kit задаёт сам — цвет отметки события ставится поверх него с важностью
      <ItemDescription key={detail.label} className="w-full wrap-break-word text-(--color-chat-event)!">
        {`${detail.label}: ${detail.value}`}
      </ItemDescription>
    ))}
  </Item>
);
